import { Card } from "react-bootstrap";
import FormTextField from "@components/Forms/FormTextField";
import { Select2V2 } from "@components/FormsV2";
import { Button, Form } from "react-bootstrap";
import { useState, ChangeEvent, useEffect } from "react";
import "../styles.css";
import { User } from "app/javascript/src/types";
import {
  handleAlert,
  formatUnprocessibleResponse,
} from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import useWhoAmI from "../api/useWhoAmI";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import {
  useV2GetUserQuery,
  useV2UpdateUserMutation,
  V2UserResourceWrite,
} from "@generated";
import PreferredDefaultsFields, {
  preferenceRelationship,
  readUserPreferences,
} from "@components/PreferredDefaultsFields";

const CompanyInfo = (): JSX.Element => {
  const [updateUser] = useV2UpdateUserMutation();

  const { data: user, error: userError, isLoading: userLoading } = useWhoAmI();

  const isLoading = userLoading;

  // Preferred rate table / currency win over the account defaults when a
  // project is created, so they must be visible and clearable here.
  const { data: userDetail, refetch: refetchUserDetail } = useV2GetUserQuery(
    {
      id: String(user?.id),
      include: ["rate-table", "currency"],
    },
    { skip: !user?.id }
  );
  const savedPreferences = readUserPreferences(userDetail);
  const [rateTableId, setRateTableId] = useState<string | null>(null);
  const [currencyId, setCurrencyId] = useState<string | null>(null);

  useEffect(() => {
    setRateTableId(savedPreferences.rateTableId);
    setCurrencyId(savedPreferences.currencyId);
  }, [savedPreferences.rateTableId, savedPreferences.currencyId]);

  const preferencesChanged =
    rateTableId !== savedPreferences.rateTableId ||
    currencyId !== savedPreferences.currencyId;

  const startPageOptions = [
    "Home",
    "Projects",
    "Approvals",
    "Surveys",
    "Settings",
  ].map((opt) => {
    return { label: opt, value: opt };
  });

  const [initialPersonalInfo, setInitialPersonalInfo] = useState<User>({
    id: "",
    type: "users",
    attributes: {
      name: "",
      email: "",
      phone: "",
      startPage: "Home",
      title: "",
    },
  });

  const [firstLastName, setFirstLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("Home");
  const [startPage, setStartPage] = useState("");
  const [password, setPassword] = useState("");
  const [passwordRequired, setPasswordRequired] = useState(false);

  // Toasts
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [errorMessages, setErrorMessages] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const successMessage = "Personal info updated successfully!";

  useEffect(() => {
    if (!isLoading && user) {
      const {
        attributes: { name: userFullName, email, phone, startPage, title },
      } = user;

      setInitialPersonalInfo({
        id: user.id,
        type: "users",
        attributes: {
          name: userFullName || "",
          email: email || "",
          phone: phone || "",
          startPage: startPage || "Home",
          title: title || "",
        },
      });

      setFirstLastName(userFullName || "");
      setEmail(email || "");
      setPhone(phone || "");
      setStartPage(startPage || "Home");
    }
  }, [isLoading]);

  const submitPersonalInfo = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user) return; // early return if no user data is available

    type StartPage = "Home" | "Projects" | "Approvals" | "Surveys" | "Settings";

    const data: V2UserResourceWrite = {
      id: String(user.id),
      type: "users",
      attributes: {
        name: firstLastName || "",
        email: email || "",
        phone: phone || "",
        "start-page": (startPage as StartPage) || "Home",
        title: user.attributes.title || "",
        "password-confirmation": password || "",
      },
      // Only once the saved values are known, so a save can't clear them.
      ...(userDetail
        ? {
            relationships: {
              "rate-table": preferenceRelationship(rateTableId, "rate-tables"),
              currency: preferenceRelationship(currencyId, "currencies"),
            },
          }
        : {}),
    };

    updateUser({ id: String(user.id), body: { data } })
      .then((res) => {
        //@ts-ignore
        if (res?.data?.data?.id) {
          setShowSuccessAlert(true);
          setPassword("");
          if (userDetail) refetchUserDetail();
        } else {
          setErrorMessages(
            //@ts-ignore
            res?.error?.data?.errors?.[0]?.detail ||
              "Something went wrong, changes were not saved."
          );
          setShowFailAlert(true);
        }
      })
      .catch((error) => {
        setErrorMessages(formatUnprocessibleResponse(error, "company info"));
        setShowFailAlert(true);
      });
  };

  const isPersonalInfoChanged =
    JSON.stringify(initialPersonalInfo) !==
    JSON.stringify({
      id: user?.id,
      type: "users",
      attributes: {
        name: firstLastName || "",
        email: email || "",
        phone: phone || "",
        startPage: startPage || "Home",
        title: user?.attributes.title || "",
      },
    });

  return (
    <div className="companyInfo">
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <Card className="whiteBg">
        <Card.Header className="headerOverride">Personal Info</Card.Header>
        <hr className="cardHeadDivider" />
        <Card.Body className="whiteBg">
          <Form onSubmit={submitPersonalInfo}>
            <FormTextField
              id="firstLastName"
              required={true}
              label="First & Last Name"
              value={firstLastName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFirstLastName(e.target.value)
              }
              placeholder={"Name"}
              readOnly={false}
            />
            <FormTextField
              id="streetAddress"
              required={true}
              label="Email"
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                setPasswordRequired(true);
              }}
              placeholder={"Email"}
              readOnly={false}
            />
            <FormTextField
              id="phone"
              required={false}
              label="Phone"
              value={phone}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPhone(e.target.value)
              }
              placeholder={"Phone"}
              readOnly={false}
            />
            <Select2V2
              label="Start Page"
              value={startPageOptions?.find((opt) => opt.value === startPage)}
              options={startPageOptions}
              onChange={(e) => setStartPage(e.value)}
            />
            <PreferredDefaultsFields
              rateTableId={rateTableId}
              currencyId={currencyId}
              currentRateTable={savedPreferences.currentRateTable}
              currentCurrency={savedPreferences.currentCurrency}
              onRateTableChange={setRateTableId}
              onCurrencyChange={setCurrencyId}
            />
            {passwordRequired && (
              <Form.Group className="formGroup">
                <FormFieldLabel className={""} label="*Current Password" />
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  placeholder={"Confirm password to save changes"}
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  required={passwordRequired}
                />
              </Form.Group>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={
                !(
                  (isPersonalInfoChanged || preferencesChanged) &&
                  initialPersonalInfo.attributes.email === email
                ) &&
                !(password && initialPersonalInfo.attributes.email !== email)
              }
            >
              Save
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CompanyInfo;
